import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Target, Zap, BarChart3, Users, Globe, Clock, CheckCircle2, Palmtree, Waves, Sun } from "lucide-react";
import livestayLogo from "@/assets/live-stay-logo.png";
import paradiseHero from "@/assets/paradise-hero.jpg";
import paradiseRelax from "@/assets/paradise-relax.jpg";

const PartnerParadisePage = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate("/cadastro-parceiro");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-muted/30 to-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <nav className="container mx-auto flex h-16 items-center justify-between px-4">
          <div 
            className="flex items-center hover:opacity-90 transition-all hover:scale-105 cursor-pointer"
            onClick={() => navigate("/")}
          >
            <img 
              src={livestayLogo} 
              alt="Live Stay" 
              className="h-16 w-auto drop-shadow-lg"
            />
          </div>
          <Button 
            variant="default" 
            size="lg" 
            className="bg-orange-500 hover:bg-orange-600"
            onClick={handleGetStarted}
          >
            Teste Grátis por 30 Dias
          </Button>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-primary/10 opacity-60"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url(${paradiseHero})` }}
        ></div>
        <div className="container mx-auto px-4 py-24 md:py-32 relative z-10">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Palmtree className="w-8 h-8 text-primary" />
              <Waves className="w-8 h-8 text-primary" />
              <Sun className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground leading-tight">
              Seu Negócio no Paraíso dos <span className="text-primary">Hóspedes</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Enquanto turistas relaxam em resorts paradisíacos, seu negócio pode estar à disposição deles. 
              Alcance quem busca o melhor da região durante suas férias dos sonhos.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button 
                variant="default" 
                size="lg" 
                className="bg-orange-500 hover:bg-orange-600 text-lg px-8 py-6"
                onClick={handleGetStarted}
              >
                Comece Grátis Agora
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className="border-primary text-primary hover:bg-primary/10 text-lg px-8 py-6"
                onClick={() => {
                  document.getElementById("como-funciona")?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Saiba Mais
              </Button>
            </div>
            <p className="text-sm text-muted-foreground pt-2">
              ✨ 30 dias grátis • Sem cartão de crédito • Cancele quando quiser
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Overview */}
      <section className="py-16 md:py-24 bg-background/50 backdrop-blur-sm">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
              Por Que Ser Parceiro Live Stay?
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Conecte seu negócio com turistas que estão vivendo o momento perfeito de relaxamento 
              e prontos para descobrir o melhor da região
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="bg-card p-6 rounded-lg border border-primary/20 hover:border-primary transition-all hover:shadow-lg">
              <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-foreground">Hóspedes Precisando de Você</h3>
              <p className="text-muted-foreground">
                Turistas em resorts buscam serviços locais: restaurantes, farmácias, passeios e muito mais.
              </p>
            </div>

            <div className="bg-card p-6 rounded-lg border border-primary/20 hover:border-primary transition-all hover:shadow-lg">
              <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <Waves className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-foreground">Momento Perfeito</h3>
              <p className="text-muted-foreground">
                Alcance clientes no momento de lazer, quando estão abertos a novas experiências.
              </p>
            </div>

            <div className="bg-card p-6 rounded-lg border border-primary/20 hover:border-primary transition-all hover:shadow-lg">
              <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <BarChart3 className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-foreground">Resultados Visíveis</h3>
              <p className="text-muted-foreground">
                Acompanhe visualizações, cliques e contatos gerados pela sua divulgação.
              </p>
            </div>

            <div className="bg-card p-6 rounded-lg border border-primary/20 hover:border-primary transition-all hover:shadow-lg">
              <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-foreground">Simples e Intuitivo</h3>
              <p className="text-muted-foreground">
                Plataforma fácil de usar. Configure seu perfil em minutos e comece a receber clientes.
              </p>
            </div>

            <div className="bg-card p-6 rounded-lg border border-primary/20 hover:border-primary transition-all hover:shadow-lg">
              <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <Globe className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-foreground">Vários Resorts</h3>
              <p className="text-muted-foreground">
                Seu negócio visível para hóspedes de diversos resorts.
              </p>
            </div>

            <div className="bg-card p-6 rounded-lg border border-primary/20 hover:border-primary transition-all hover:shadow-lg">
              <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-foreground">Sempre Disponível</h3>
              <p className="text-muted-foreground">
                Sua divulgação funcionando 24 horas por dia, 7 dias por semana.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-primary/5"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <img 
                src={paradiseRelax} 
                alt="Relaxamento em resort" 
                className="rounded-2xl shadow-lg w-full"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                Perfeito Para Seu Tipo de Negócio
              </h2>
              <p className="text-muted-foreground text-lg">
                Se você oferece serviços que turistas precisam durante suas férias paradisíacas, 
                o Live Stay é para você:
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "Restaurantes",
                  "Farmácias",
                  "Transfer",
                  "Passeios",
                  "Fotógrafos",
                  "Salões de Beleza",
                  "Massagem",
                  "Aluguel de Equipamentos",
                  "Supermercados",
                  "Padarias",
                  "Disk Bebidas",
                  "E muito mais..."
                ].map((business) => (
                  <div key={business} className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-foreground">{business}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="como-funciona" className="py-16 md:py-24 bg-background/80">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
              É Muito Simples!
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Comece a alcançar hóspedes de resorts em apenas 3 passos
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center space-y-4">
              <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto border-2 border-primary">
                <span className="text-2xl font-bold text-primary">1</span>
              </div>
              <h3 className="text-xl font-semibold text-foreground">Cadastre-se Grátis</h3>
              <p className="text-muted-foreground">
                Crie sua conta sem custo e sem compromisso. Teste por 30 dias.
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto border-2 border-primary">
                <span className="text-2xl font-bold text-primary">2</span>
              </div>
              <h3 className="text-xl font-semibold text-foreground">Adicione Seu Negócio</h3>
              <p className="text-muted-foreground">
                Configure seu perfil com fotos, descrição, localização e contatos.
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto border-2 border-primary">
                <span className="text-2xl font-bold text-primary">3</span>
              </div>
              <h3 className="text-xl font-semibold text-foreground">Receba os Hóspedes</h3>
              <p className="text-muted-foreground">
                Turistas verão seu negócio e entrarão em contato quando precisarem.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-primary/5 opacity-20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center space-y-6 bg-card/80 backdrop-blur-sm p-12 rounded-2xl border border-primary/30 shadow-lg">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Palmtree className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              Pronto Para Alcançar Mais Clientes?
            </h2>
            <p className="text-lg text-muted-foreground">
              Comece agora sua divulgação gratuita por 30 dias. Sem cartão de crédito, sem compromisso.
            </p>
            <Button 
              variant="default" 
              size="lg" 
              className="bg-orange-500 hover:bg-orange-600 text-lg px-10 py-6"
              onClick={handleGetStarted}
            >
              Começar Teste Grátis Agora
            </Button>
            <p className="text-sm text-muted-foreground">
              Junte-se aos negócios locais que estão crescendo com o Live Stay
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/40 bg-background/80 backdrop-blur-sm py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Live Stay. Todos os direitos reservados.
            </div>
            <div className="flex gap-6 text-sm text-muted-foreground">
              <a 
                href="/termos" 
                className="hover:text-primary transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/termos");
                }}
              >
                Termos de Uso
              </a>
              <a 
                href="/privacidade" 
                className="hover:text-primary transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/privacidade");
                }}
              >
                Privacidade
              </a>
              <a 
                href="/fale-conosco" 
                className="hover:text-primary transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/fale-conosco");
                }}
              >
                Contato
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PartnerParadisePage;

